import React, { useState } from "react";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import {
    Grid,
    Stack,
    styled,
    useTheme,
    IconButton,
    Tooltip
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import ProfileButton from "./ProfileButton";
import { useLocation } from "react-router-dom";
import { BaseUser } from "../types";

// These should be provided by the consuming app
interface TopMenuDependencies {
    NeonAppBar?: React.ComponentType<any>;
    getNeonColor?: (theme: any) => string;
}

// Create BoldButton as a function that accepts getNeonColor
const createBoldButton = (getNeonColorFn: (theme: any) => string = (theme: any) => theme.palette.primary.main) => {
    return styled(Button)<{ active?: boolean }>(({ theme, active }) => {
        const neonColor = getNeonColorFn(theme);
        return {
            fontWeight: 'bold',
            justifyContent: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            padding: '6px 10px',
            margin: '1px 0',
            borderRadius: '4px',
            textTransform: 'none',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            fontSize: '0.875rem',
            '&:hover': {
                textShadow: `0px 0px 2px ${neonColor}`,
                backgroundColor: `${neonColor}15`,
            },
            ...(active && {
                textShadow: `0px 0px 4px ${neonColor}`,
                color: neonColor,
                backgroundColor: `${neonColor}20`,
            })
        };
    });
};

interface MenuItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    menuContent: React.ReactNode | ((onClose: () => void) => React.ReactNode);
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    activeSectionMatcher?: (pathname: string) => boolean;
}

interface TopMenuProps {
    logout: () => void;
    user: BaseUser;
    menuItems: MenuItem[];
    searchComponent?: React.ReactNode;
    additionalActions?: React.ReactNode;
    teamIconComponent?: React.ComponentType<{ team_name: string; sx?: any }>;
    routes?: {
        profile?: string;
        settings?: string;
        manageOrg?: string;
        staffDashboard?: string;
        playerDashboard?: string;
        metricsDetails?: string;
        utils?: string;
    };
    dependencies?: TopMenuDependencies;
}

export default function TopMenu(props: TopMenuProps) {
    const [menuOpen, setMenuOpen] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const location = useLocation();
    const theme = useTheme();
    
    // Get dependencies with fallbacks
    const NeonAppBar = props.dependencies?.NeonAppBar || (({ children, ...props }: any) => <div {...props}>{children}</div>);
    const getNeonColor = props.dependencies?.getNeonColor || ((theme: any) => theme.palette.primary.main);
    
    // Memoize to avoid recreating the styled component on every render,
    // which would cause React to remount buttons and invalidate anchorEl refs.
    const BoldButton = React.useMemo(() => createBoldButton(getNeonColor), [getNeonColor]);

    // Determine active section based on menu items' matchers
    const getActiveSection = (): string | null => {
        for (const item of props.menuItems) {
            if (item.activeSectionMatcher && item.activeSectionMatcher(location.pathname)) {
                return item.id;
            }
        }
        return null;
    };

    const activeSection = getActiveSection();

    const handleMenuClick = (itemId: string, event: React.MouseEvent<HTMLElement>) => {
        if (props.menuItems.find(item => item.id === itemId)?.onClick) {
            props.menuItems.find(item => item.id === itemId)?.onClick?.(event);
        }
        setAnchorEl(event.currentTarget);
        setMenuOpen(menuOpen === itemId ? null : itemId);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setMenuOpen(null);
    };

    const NeonAppBarComponent = NeonAppBar as any;
    
    return (
        <NeonAppBarComponent position="relative" sx={{ height: "6%" }}>
            <Toolbar sx={{ width: "100%", background: theme.palette.background.paper }}>
                <Grid 
                    container 
                    columns={{xl: 12, xs: 6, sm: 6, md: 12, lg: 12}}
                    direction={"row"}
                    sx={{
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                    spacing={1}
                >
                    <Grid>
                        <Stack direction={'row'} sx={{ alignItems: 'center' }}>
                            <ProfileButton 
                                logout={props.logout}
                                user={props.user}
                                teamIconComponent={props.teamIconComponent}
                                routes={props.routes}
                            />
                            {props.teamIconComponent && (
                                <props.teamIconComponent 
                                    team_name={props.user.team} 
                                    sx={{ width: 32, height: 32 }}
                                />
                            )}
                        </Stack>
                    </Grid>

                    {/* Game-specific menu items */}
                    {props.menuItems.map((item) => (
                        <Grid key={item.id}>
                            <BoldButton
                                active={activeSection === item.id}
                                sx={{ fontWeight: 'bold' }}
                                onClick={(event) => handleMenuClick(item.id, event)}
                                aria-controls={menuOpen === item.id ? `${item.id}-menu` : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuOpen === item.id ? "true" : undefined}
                            >
                                <Stack direction={'row'} sx={{ alignItems: 'center' }} spacing={0.5}>
                                    {item.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>}
                                    <span>{item.label}</span>
                                    <ArrowDropDown />
                                </Stack>
                            </BoldButton>
                            <Menu
                                MenuListProps={{ disablePadding: true }}
                                anchorEl={anchorEl}
                                id={`${item.id}-menu`}
                                open={menuOpen === item.id}
                                onClose={handleClose}
                                sx={{ padding: 0, margin: 0 }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                                {typeof item.menuContent === 'function' 
                                    ? item.menuContent(handleClose) 
                                    : item.menuContent}
                            </Menu>
                        </Grid>
                    ))}

                    {/* Right side actions */}
                    <Grid sx={{ alignItems: 'flex-end', justifyContent: 'flex-end', display: 'flex', flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            {props.searchComponent}
                            {props.additionalActions}
                        </Stack>
                    </Grid>
                </Grid>
            </Toolbar>
        </NeonAppBarComponent>
    );
}

